import React from 'react';
import {
    FederatedSeller,
    LanguageProficiencyLevel,
    getCategoriesText,
    useSellerData,
} from '@fiverr-private/seller_pages_toolbox';
import { useSelectedRole } from '../../hooks/useSelectedRole';
import { useRoleVariation } from '../../hooks/useRoleVariation';
import { translateRoleTitle } from '../../utils/translateRoleTitle';
import { useGigsOrdered } from '../GigServices/useGigs';

export const SeoScript: React.FC = () => {
    const { displayName, oneLinerTitle, address, languages, description, proSubCategories, workExperiences } =
        useSellerData(selectSellerSlice);
    const sellerRole = useSelectedRole();
    const roleVariation = useRoleVariation();
    const gigs = useGigsOrdered(sellerRole?.id);

    const roleTitle = sellerRole ? translateRoleTitle(sellerRole, roleVariation[sellerRole.id]) : oneLinerTitle;

    const country = address?.countryCode;

    const validLanguages = React.useMemo(
        () => languages.filter((lang) => lang.level !== LanguageProficiencyLevel.Basic),
        [languages]
    );

    const subcategoriesNames = React.useMemo(() => getCategoriesText(proSubCategories), [proSubCategories]);

    const workExperience = React.useMemo(
        () =>
            workExperiences?.map((workExperience) => ({
                name: workExperience.title,
                skills: workExperience.skills,
                responsibilities: [workExperience.description],
            })),
        [workExperiences]
    );

    const services = gigs.map((gig) => ({
        name: gig.title,
        url: gig.gig_url,
        reviewRating: {
            ratingValue: gig.buying_review_rating,
        },
    }));

    const scriptContent = {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: displayName,
        jobTitle: roleTitle,
        nationality: {
            '@type': 'Country',
            name: country,
        },
        knowsLanguage: validLanguages,
        description: sellerRole?.description || description,
        knowsAbout: subcategoriesNames,
        hasOccupation: workExperience?.map(({ name, skills, responsibilities }) => ({
            '@type': 'Occupation',
            name,
            skills,
            responsibilities,
        })),
        gigs,
        hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'Services Offered',
            itemListElement: services.map((gig) => ({
                '@type': 'Offer',
                itemOffered: {
                    '@type': 'Service',
                    name: gig.name,
                    url: gig.url,
                    areaServed: 'Worldwide',
                    review: {
                        '@type': 'Review',
                        reviewRating: {
                            '@type': 'Rating',
                            ratingValue: gig.reviewRating.ratingValue,
                        },
                    },
                },
            })),
        },
    };

    return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(scriptContent) }} />;
};

function selectSellerSlice(seller: FederatedSeller) {
    return {
        displayName: seller.user.profile.displayName,
        oneLinerTitle: seller.oneLinerTitle,
        address: seller.user.address,
        languages: seller.user.languages,
        description: seller.description,
        proSubCategories: seller.proSubCategories,
        workExperiences: seller.workExperiences.nodes,
    };
}
